import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

const TITLE_OPTIONS = ['', 'Caregiver', 'Executive Director', 'Maintenance Director', 'Wellness Director',
  'Director of Nursing', 'Nurse', 'Other'];

// Password rules live in utils/passwordRules.js, shared by every screen that sets one.
import {
  PASSWORD_MIN_LENGTH, PASSWORD_PATTERN_ATTR, PASSWORD_RULE_HINT, ADMIN_WEAK_PASSWORD_MSG,
  isPasswordFormatValid, isAdminWeakPassword as isWeakForRole,
} from '../../../utils/passwordRules.js';
import Drawer from '../../../components/v2/Drawer.jsx';

/**
 * UserDrawer — slide-out drawer for Add / Edit user.
 * Includes role-weight authorization, group picker, and password reset section.
 *
 * @param {{
 *   isOpen: boolean,
 *   onClose: () => void,
 *   user: object | null,                // null = Add mode, object = Edit mode
 *   onSave: (data: object) => void,
 *   onDelete: (user: object) => void,
 *   onResetPassword: (data: object) => void,
 *   roles: Array<{ user_role_id: number, user_role_name: string, user_role_weight: number }>,
 *   groups: Array<{ group_id: number, group_name: string, capcode: string }>,
 *   myRoleWeight: number,
 *   myUsername: string,
 *   displayEditAllowed: boolean,
 *   iAmHigherAuthority: (comparedWeight: number, myWeight: number) => boolean
 * }} props
 */
const UserDrawer = ({
  isOpen, onClose, user, onSave, onDelete, onResetPassword,
  roles, groups, schedules, myRoleWeight, myUsername, displayEditAllowed,
  iAmHigherAuthority,
}) => {
  const isEditMode = !!user;
  const isSelfEdit = isEditMode && user?.username === myUsername;
  const drawerRef = useRef(null);

  // Form state
  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [title, setTitle] = useState('');
  const [titleCustom, setTitleCustom] = useState('');
  const [roleId, setRoleId] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [groupSearch, setGroupSearch] = useState('');
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  // Scheduled reports state
  const [selectedReportIds, setSelectedReportIds] = useState(new Set());
  const [initialReportIds, setInitialReportIds] = useState(new Set());
  const [reportSearch, setReportSearch] = useState('');

  // Password reset state (edit mode only)
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  // Scheduled reports list with search filtering
  const scheduleOptions = useMemo(() => schedules || [], [schedules]);
  const filteredSchedules = useMemo(() => {
    if (!reportSearch) return scheduleOptions;
    const term = reportSearch.toLowerCase();
    return scheduleOptions.filter((s) =>
      (s.description || '').toLowerCase().includes(term) ||
      (s.report_type_name || '').toLowerCase().includes(term)
    );
  }, [scheduleOptions, reportSearch]);

  // Populate form on open/user change
  useEffect(() => {
    if (isOpen && user) {
      setUsername(user.username || '');
      setDisplayName(user.display_name || '');
      setEmail(user.email || '');
      setPhone(user.phone || '');
      setPassword('');
      setConfirmPassword('');
      setTitle(
        TITLE_OPTIONS.includes(user.title) ? (user.title || '') : 'Other'
      );
      setTitleCustom(
        TITLE_OPTIONS.includes(user.title) ? '' : (user.title || '')
      );
      setRoleId(user.user_role_id?.toString() || '');
      setSelectedGroupIds(
        (user.group_ids || []).map((g) =>
          (typeof g === 'object' && g !== null ? g.group_id : g)?.toString() || ''
        ).filter(Boolean)
      );
      setGroupSearch('');
      setShowSelectedOnly(false);
      setShowPasswordReset(false);
      setNewPassword('');
      setConfirmNewPassword('');
      // Determine which schedules this user is currently attached to
      const userIdStr = String(user.user_id);
      const attached = new Set(
        (schedules || [])
          .filter((s) => {
            const ids = (s.report_user_ids || '').split(',').map((id) => id.trim()).filter(Boolean);
            return ids.includes(userIdStr);
          })
          .map((s) => String(s.report_id))
      );
      setSelectedReportIds(attached);
      setInitialReportIds(attached);
      setReportSearch('');
    } else if (isOpen && !user) {
      setUsername('');
      setDisplayName('');
      setEmail('');
      setPhone('');
      setPassword('');
      setConfirmPassword('');
      setTitle('');
      setTitleCustom('');
      setRoleId('');
      setSelectedGroupIds([]);
      setGroupSearch('');
      setShowSelectedOnly(false);
      setShowPasswordReset(false);
      setNewPassword('');
      setConfirmNewPassword('');
      setSelectedReportIds(new Set());
      setInitialReportIds(new Set());
      setReportSearch('');
    }
  }, [isOpen, user, schedules]);

  // Focus first input on open
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const firstInput = drawerRef.current?.querySelector('input, select');
        firstInput?.focus();
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Filtered roles: only show roles the current user has authority over.
  // Exception: Administrators (role_weight 20) may pick their own role on Add only.
  const filteredRoles = useMemo(() => {
    if (!roles) return [];
    return roles.filter((role) => {
      if (iAmHigherAuthority(role.user_role_weight, myRoleWeight)) return true;
      if (!isEditMode && myRoleWeight === 20 && role.user_role_weight === 20) return true;
      return false;
    });
  }, [roles, myRoleWeight, iAmHigherAuthority, isEditMode]);

  // Determine if selected role is caregiver (email not required for caregivers)
  const selectedRole = useMemo(() => {
    if (!roleId || !roles) return null;
    return roles.find((r) => r.user_role_id.toString() === roleId) || null;
  }, [roleId, roles]);

  const isCaregiver = selectedRole?.user_role_name?.toLowerCase() === 'caregiver';

  // Filter groups by search and "show selected" toggle
  const filteredGroups = useMemo(() => {
    let list = groups || [];
    if (showSelectedOnly) {
      list = list.filter((g) => selectedGroupIds.includes(g.group_id.toString()));
    }
    if (groupSearch.trim()) {
      const s = groupSearch.toLowerCase();
      list = list.filter(
        (g) =>
          g.group_name?.toLowerCase().includes(s) ||
          g.capcode?.toLowerCase().includes(s)
      );
    }
    return list;
  }, [groups, groupSearch, showSelectedOnly, selectedGroupIds]);

  const handleGroupToggle = useCallback((groupId) => {
    const gStr = groupId.toString();
    setSelectedGroupIds((prev) =>
      prev.includes(gStr) ? prev.filter((id) => id !== gStr) : [...prev, gStr]
    );
  }, []);

  const handleSelectAllGroups = useCallback(() => {
    const allFilteredIds = filteredGroups.map((g) => g.group_id.toString());
    setSelectedGroupIds((prev) => [...new Set([...prev, ...allFilteredIds])]);
  }, [filteredGroups]);

  const handleClearGroups = useCallback(() => {
    setSelectedGroupIds([]);
  }, []);

  const handleReportToggle = useCallback((reportId) => {
    setSelectedReportIds((prev) => {
      const next = new Set(prev);
      const id = String(reportId);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  // Adds the visible reports to the selection, like Select All on groups above. It used
  // to replace it, so picks hidden by the search were silently dropped (A26).
  const handleSelectAllReports = useCallback(() => {
    setSelectedReportIds((prev) => new Set([...prev, ...filteredSchedules.map((s) => String(s.report_id))]));
  }, [filteredSchedules]);

  const handleClearReports = useCallback(() => {
    setSelectedReportIds(new Set());
  }, []);

  // Check if admin role (role_id 1 or 2) is using weak password
  const isAdminWeakPassword = useMemo(() => {
    return !isEditMode && isWeakForRole(password, roleId);
  }, [roleId, password, isEditMode]);

  // Form validation
  const isFormValid = useMemo(() => {
    if (isEditMode) {
      return displayName.trim() && roleId;
    }
    // Add mode
    if (!username.trim()) return false;
    if (!displayName.trim()) return false;
    if (!isPasswordFormatValid(password)) return false;
    if (password !== confirmPassword) return false;
    if (!roleId) return false;
    if (!isCaregiver && !email.trim()) return false;
    if (isAdminWeakPassword) return false;
    return true;
  }, [isEditMode, username, displayName, email, password, confirmPassword, roleId, isCaregiver, isAdminWeakPassword]);

  // Password reset validation
  const isPasswordResetValid = useMemo(() => {
    if (!isPasswordFormatValid(newPassword)) return false;
    if (newPassword !== confirmNewPassword) return false;
    // Admin weak password check for reset
    if (isWeakForRole(newPassword, roleId)) return false;
    return true;
  }, [newPassword, confirmNewPassword, roleId]);

  // Can current user reset password for this user?
  const canResetPassword = useMemo(() => {
    if (!isEditMode) return false;
    if (isSelfEdit) return true;
    return iAmHigherAuthority(user?.user_role_weight, myRoleWeight);
  }, [isEditMode, isSelfEdit, user, myRoleWeight, iAmHigherAuthority]);

  // Can current user delete this user?
  const canDelete = useMemo(() => {
    if (!isEditMode || !user) return false;
    if (isSelfEdit) return false;
    return iAmHigherAuthority(user?.user_role_weight, myRoleWeight);
  }, [isEditMode, user, isSelfEdit, myRoleWeight, iAmHigherAuthority]);

  // Build reports array for the payload
  const buildReportsPayload = useCallback(() => {
    const allSchedules = schedules || [];
    const reports = [];

    if (isEditMode) {
      // Edit mode — only include reports that changed (added or removed)
      const userIdStr = String(user?.user_id);
      allSchedules.forEach((s) => {
        const reportIdStr = String(s.report_id);
        const wasSelected = initialReportIds.has(reportIdStr);
        const isSelected = selectedReportIds.has(reportIdStr);
        if (wasSelected !== isSelected) {
          // This report changed — compute the new report_user_ids
          const currentIds = (s.report_user_ids || '').split(',').map((id) => id.trim()).filter(Boolean);
          let newIds;
          if (isSelected) {
            // Added user to this report
            newIds = [...new Set([...currentIds, userIdStr])];
          } else {
            // Removed user from this report
            newIds = currentIds.filter((id) => id !== userIdStr);
          }
          reports.push({ report_id: s.report_id, report_user_ids: newIds.join(',') });
        }
      });
    } else {
      // Add mode — include all selected reports with their current report_user_ids
      // Backend will append the newly created user's id to each
      allSchedules.forEach((s) => {
        if (selectedReportIds.has(String(s.report_id))) {
          reports.push({ report_id: s.report_id, report_user_ids: s.report_user_ids || '' });
        }
      });
    }

    return reports;
  }, [schedules, isEditMode, user, selectedReportIds, initialReportIds]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setSaving(true);

      const formData = {
        user_id: user?.user_id || null,
        display_name: displayName.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.replace(/-/g, ''),
        user_role_id: parseInt(roleId, 10),
        group_ids: selectedGroupIds.map((id) => id.toString()),
        title: title === 'Other' ? titleCustom.trim() : title,
        cognito_id: user?.cognito_id,
        // Cognito always stores usernames lowercase — normalize here so the
        // portal record matches. Covers both add (new username) and edit
        // (corrects any legacy mixed-case username on save).
        username: username.trim().toLowerCase(),
        reports: buildReportsPayload(),
      };

      // Only include password for add mode (username is always needed)
      if (!isEditMode) {
        formData.password = password;
      }

      try {
        await onSave(formData);
      } finally {
        setSaving(false);
      }
    },
    [user, username, displayName, email, phone, password, roleId, selectedGroupIds, selectedReportIds, title, titleCustom, isEditMode, onSave, buildReportsPayload]
  );

  const handlePasswordReset = useCallback(() => {
    if (!isPasswordResetValid || !user) return;
    setSavingPassword(true);

    setTimeout(() => {
      onResetPassword({
        user_id: user.user_id,
        username: user.username,
        password: newPassword,
      });
      setSavingPassword(false);
      setNewPassword('');
      setConfirmNewPassword('');
      setShowPasswordReset(false);
    }, 400);
  }, [isPasswordResetValid, user, newPassword, onResetPassword]);

  /* Hands off to the page, which opens the shared typed confirmation. This used
     to confirm inline in the footer; that was replaced so deleting a user asks
     the same way from here as from the table kebab, and so there is exactly one
     confirmation rather than two. The drawer stays open behind the dialog, so
     cancelling keeps unsaved edits. */
  const handleDeleteClick = useCallback(() => {
    if (user && onDelete) onDelete(user);
  }, [user, onDelete]);

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      classPrefix="up"
      size="lg"
      panelRef={drawerRef}
      ariaLabel={isEditMode ? 'Edit User' : 'New User'}
      title={isEditMode ? 'Edit User' : 'New User'}
      rawBody
    >
      {/* Scrollable form body */}
      <form onSubmit={handleSubmit} className="up-form">
        <div className="up-form__body">

          {/* ── Role & Title Section ── */}
          <div className="up-form-section">
            <h3 className="up-form-section__title">Role &amp; Title</h3>

            {/* User Role */}
            <div>
              <label htmlFor="drawer-role" className="up-field__label">
                User Role <span className="up-field__required">*</span>
              </label>
              <select
                id="drawer-role"
                value={roleId}
                onChange={(e) => setRoleId(e.target.value)}
                required
                disabled={isSelfEdit}
                className="up-field__select"
              >
                <option value="">Select role...</option>
                {filteredRoles.map((role) => (
                  <option key={role.user_role_id} value={role.user_role_id}>
                    {role.user_role_name}
                  </option>
                ))}
              </select>
              {isSelfEdit ? (
                <p className="up-field__hint">You cannot change your own role.</p>
              ) : null}
            </div>

            {/* Title */}
            <div>
              <label htmlFor="drawer-title" className="up-field__label">
                Title
              </label>
              <select
                id="drawer-title"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (e.target.value !== 'Other') setTitleCustom('');
                }}
                className="up-field__select"
              >
                {TITLE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt || 'Select title...'}
                  </option>
                ))}
              </select>
              {title === 'Other' ? (
                <input
                  type="text"
                  id="drawer-title-custom"
                  value={titleCustom}
                  onChange={(e) => setTitleCustom(e.target.value)}
                  className="up-field__input"
                  placeholder="Enter custom title"
                  style={{ marginTop: '0.5rem' }}
                />
              ) : null}
            </div>
          </div>

          {/* ── User Details Section ── */}
          <div className="up-form-section">
            <h3 className="up-form-section__title">User Details</h3>

            {/* Username (add mode only) */}
            {!isEditMode ? (
              <div>
                <label htmlFor="drawer-username" className="up-field__label">
                  Username <span className="up-field__required">*</span>
                </label>
                <input
                  type="text"
                  id="drawer-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase())}
                  required
                  pattern="^[a-z0-9.\-_@]+$"
                  className="up-field__input"
                  placeholder="Enter username"
                />
              </div>
            ) : null}

            {/* Display Name */}
            <div>
              <label htmlFor="drawer-display-name" className="up-field__label">
                Display Name <span className="up-field__required">*</span>
              </label>
              <input
                type="text"
                id="drawer-display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                disabled={isSelfEdit && !displayEditAllowed}
                className="up-field__input"
                placeholder="Enter display name"
              />
              {isSelfEdit && !displayEditAllowed ? (
                <p className="up-field__hint">Display name editing is not enabled for your account.</p>
              ) : null}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="drawer-email" className="up-field__label">
                Email {!isCaregiver ? <span className="up-field__required">*</span> : null}
              </label>
              <input
                type="email"
                id="drawer-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required={!isCaregiver}
                className="up-field__input"
                placeholder="email@example.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="drawer-phone" className="up-field__label">
                Phone
              </label>
              <input
                type="tel"
                id="drawer-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                pattern="[0-9]{3}-?[0-9]{3}-?[0-9]{4}"
                className="up-field__input"
                placeholder="555-555-5555"
              />
            </div>

            {/* Password fields (add mode only) */}
            {!isEditMode ? (
              <>
                <div>
                  <label htmlFor="drawer-password" className="up-field__label">
                    Password <span className="up-field__required">*</span>
                  </label>
                  <input
                    type="password"
                    id="drawer-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    pattern={PASSWORD_PATTERN_ATTR}
                    className="up-field__input"
                    placeholder="Minimum 6 characters, letters and numbers only"
                    title={PASSWORD_RULE_HINT}
                  />
                  {password && !isPasswordFormatValid(password) ? (
                    <p className="up-field__error">{PASSWORD_RULE_HINT}</p>
                  ) : (
                    <p className="up-field__hint">{PASSWORD_RULE_HINT}</p>
                  )}
                  {isAdminWeakPassword ? (
                    <p className="up-field__error">{ADMIN_WEAK_PASSWORD_MSG}</p>
                  ) : null}
                </div>
                <div>
                  <label htmlFor="drawer-confirm-password" className="up-field__label">
                    Confirm Password <span className="up-field__required">*</span>
                  </label>
                  <input
                    type="password"
                    id="drawer-confirm-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={PASSWORD_MIN_LENGTH}
                    className="up-field__input"
                    placeholder="Re-enter password"
                  />
                  {confirmPassword && password !== confirmPassword ? (
                    <p className="up-field__error">Passwords do not match.</p>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>

          {/* ── Groups Section ── */}
          <div className="up-form-section">
            <h3 className="up-form-section__title">Groups</h3>
          <div className="up-user-picker">
            <div className="up-user-picker__header">
              <p className="up-field__label" style={{ marginBottom: 0 }}>
                <span className="up-user-picker__count">
                  ({selectedGroupIds.length} selected)
                </span>
              </p>
            </div>

            {/* Search */}
            <div className="up-user-picker__search">
              <i className="fe fe-search up-user-picker__search-icon" aria-hidden="true" />
              <input
                type="text"
                placeholder="Search groups..."
                value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                className="up-user-picker__search-input"
              />
            </div>

            {/* Select All / Clear / Show Selected */}
            <div className="up-user-picker__actions">
              <button
                type="button"
                onClick={handleSelectAllGroups}
                className="up-user-picker__action-btn"
              >
                Select All
              </button>
              <button
                type="button"
                onClick={handleClearGroups}
                className="up-user-picker__action-btn"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setShowSelectedOnly((prev) => !prev)}
                className={`up-user-picker__action-btn${showSelectedOnly ? ' up-user-picker__action-btn--active' : ''}`}
              >
                Show Selected
              </button>
            </div>

            {/* Group list */}
            <div className="up-user-picker__list">
              {filteredGroups.length === 0 ? (
                <p className="up-user-picker__empty">
                  {showSelectedOnly ? 'No selections.' : 'No groups found'}
                </p>
              ) : (
                filteredGroups.map((g) => {
                  const isChecked = selectedGroupIds.includes(g.group_id.toString());
                  return (
                    <label
                      key={g.group_id}
                      className={`up-user-picker__item ${isChecked ? 'is-selected' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleGroupToggle(g.group_id)}
                        className="up-checkbox"
                      />
                      <div className="up-user-picker__item-info">
                        <span className="up-user-picker__item-name">{g.group_name}</span>
                        <span className="up-user-picker__item-type">
                          {g.capcode || ''}
                        </span>
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>
          </div>{/* end Groups section */}

          {/* ── Scheduled Reports Section ── */}
          {scheduleOptions.length > 0 ? (
            <div className="up-form-section">
              <h3 className="up-form-section__title">Scheduled Reports</h3>
            <div className="up-user-picker">
              <div className="up-user-picker__header">
                <p className="up-field__label" style={{ marginBottom: 0 }}>
                  <span className="up-user-picker__count">
                    ({selectedReportIds.size} selected)
                  </span>
                </p>
              </div>

              {/* Search */}
              <div className="up-user-picker__search">
                <i className="fe fe-search up-user-picker__search-icon" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Search reports..."
                  value={reportSearch}
                  onChange={(e) => setReportSearch(e.target.value)}
                  className="up-user-picker__search-input"
                />
              </div>

              {/* Select All / Clear */}
              <div className="up-user-picker__actions">
                <button type="button" onClick={handleSelectAllReports} className="up-user-picker__action-btn">Select All</button>
                <button type="button" onClick={handleClearReports} className="up-user-picker__action-btn">Clear</button>
              </div>

              {/* Report list */}
              <div className="up-user-picker__list">
                {filteredSchedules.length === 0 ? (
                  <p className="up-user-picker__empty">No reports found.</p>
                ) : (
                  filteredSchedules.map((s) => {
                    const isChecked = selectedReportIds.has(String(s.report_id));
                    return (
                      <label
                        key={s.report_id}
                        className={`up-user-picker__item ${isChecked ? 'is-selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleReportToggle(s.report_id)}
                          className="up-checkbox"
                        />
                        <div className="up-user-picker__item-info">
                          <span className="up-user-picker__item-name">{s.description || '(untitled)'}</span>
                          <span className="up-user-picker__item-type">
                            {s.report_type_name || ''}
                          </span>
                        </div>
                      </label>
                    );
                  })
                )}
              </div>
            </div>
            </div>/* end Scheduled Reports section */
          ) : null}

          {/* Password Reset Section (edit mode only) */}
          {isEditMode && canResetPassword ? (
            <div className="up-form-section">
              <h3 className="up-form-section__title">Password Reset</h3>

              {!showPasswordReset ? (
                <button
                  type="button"
                  onClick={() => setShowPasswordReset(true)}
                  className="up-btn up-btn--outline"
                >
                  <i className="fe fe-lock" aria-hidden="true" />
                  Change Password
                </button>
              ) : (
                <>
                  <div>
                    <label htmlFor="drawer-new-password" className="up-field__label">
                      New Password <span className="up-field__required">*</span>
                    </label>
                    <input
                      type="password"
                      id="drawer-new-password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      minLength={PASSWORD_MIN_LENGTH}
                      pattern={PASSWORD_PATTERN_ATTR}
                      className="up-field__input"
                      placeholder="Minimum 6 characters, letters and numbers only"
                      title={PASSWORD_RULE_HINT}
                    />
                    {newPassword && !isPasswordFormatValid(newPassword) ? (
                      <p className="up-field__error">{PASSWORD_RULE_HINT}</p>
                    ) : (
                      <p className="up-field__hint">{PASSWORD_RULE_HINT}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="drawer-confirm-new-password" className="up-field__label">
                      Confirm New Password <span className="up-field__required">*</span>
                    </label>
                    <input
                      type="password"
                      id="drawer-confirm-new-password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      minLength={PASSWORD_MIN_LENGTH}
                      className="up-field__input"
                      placeholder="Re-enter new password"
                    />
                    {confirmNewPassword && newPassword !== confirmNewPassword ? (
                      <p className="up-field__error">Passwords do not match.</p>
                    ) : null}
                  </div>
                  <div className="up-password-section__actions">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPasswordReset(false);
                        setNewPassword('');
                        setConfirmNewPassword('');
                      }}
                      className="up-btn up-btn--cancel up-btn--sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handlePasswordReset}
                      disabled={!isPasswordResetValid || savingPassword}
                      className="up-btn up-btn--primary up-btn--sm"
                    >
                      {savingPassword ? (
                        <span className="up-saving-text">
                          <svg className="up-icon-md up-spinner" fill="none" viewBox="0 0 24 24">
                            <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          Resetting...
                        </span>
                      ) : (
                        'Reset Password'
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : null}
        </div>

        {/* Footer with actions */}
        <div className="up-form__footer">
          {/* Delete button (edit mode only, if authorized) — opens the
              page's typed confirmation rather than confirming here. */}
          {isEditMode && canDelete ? (
            <button
              type="button"
              onClick={handleDeleteClick}
              className="up-btn up-btn--danger"
            >
              <i className="fe fe-trash-2" aria-hidden="true" />
              Delete
            </button>
          ) : null}

          {/* Spacer pushes Cancel/Save right */}
          <div style={{ flex: 1 }} />

              <button
                type="button"
                onClick={onClose}
                className="up-btn up-btn--cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isFormValid || saving}
                className="up-btn up-btn--primary"
              >
                {saving ? (
                  <span className="up-saving-text">
                    <svg className="up-icon-md up-spinner" fill="none" viewBox="0 0 24 24">
                      <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Saving...
                  </span>
            ) : (
              isEditMode ? 'Save Changes' : 'Create User'
            )}
          </button>
        </div>
      </form>
    </Drawer>
  );
};

export default UserDrawer;
